import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '~/lib/api-client';
import { feedKeys, meKeys } from '~/lib/query-keys';
import { tenantKeys } from '~/hooks/queries/use-tenant';
import { deviceTimeZone } from '~/lib/local-datetime';
import { decideOnboardedGate, resolveDevSkipOnboarding } from '~/lib/onboarding-gate';
import type { AuthMe } from '~/hooks/queries/use-is-admin';
import { ensureCustomAgentEnabled } from './agent-activation';
import { runDevAutoOnboard } from './dev-auto-onboard';

/**
 * FRONT-1037: the dev-only `?skip-onboarding=1` switch, resolved once per
 * load and before any gate can redirect, so the param is stored even on the
 * visit that bounces to sign-in. A production build never reads it.
 */
export function useDevSkipOnboarding(): boolean {
  const [devSkip] = useState(() =>
    resolveDevSkipOnboarding({
      isDev: import.meta.env.DEV,
      search: window.location.search,
      getStorage: () => window.localStorage,
    }),
  );
  return devSkip;
}

/**
 * The wizard gate both shells (desktop and the phone surface) run: a user
 * with no identity yet is sent to the wizard at /onboard. /onboard renders
 * under AuthLayout, so this redirect doesn't loop, and the hard navigation
 * tears down any half-loaded chrome.
 *
 * With the dev switch on it runs the wizard's writes itself instead (see
 * `runDevAutoOnboard`) while the shell keeps its "Setting things up…"
 * placeholder up, then drops the server's own answer into the cache. Any
 * failure opens the real wizard, so the placeholder can never stick.
 */
export function useOnboardedGate(
  authed: boolean,
  onboarded: boolean | undefined,
  onboardedReady: boolean,
  devSkip: boolean,
): void {
  const queryClient = useQueryClient();
  const autoOnboardStarted = useRef(false);
  useEffect(() => {
    const gate = decideOnboardedGate({
      authed,
      onboardedReady,
      onboarded,
      pathname: window.location.pathname,
      devSkip,
    });
    if (gate === 'wizard') {
      window.location.replace('/onboard');
      return;
    }
    if (gate !== 'dev-auto-onboard' || autoOnboardStarted.current) return;
    autoOnboardStarted.current = true;
    runDevAutoOnboard({
      me: queryClient.getQueryData<AuthMe>(['auth', 'me']),
      timezone: deviceTimeZone(),
      deps: {
        patchIdentity: (displayName) => api.patch('/api/admin/me/identity', { displayName }),
        saveTimezone: (timezone) => api.patch('/api/admin/tenants/preferences', { timezone }),
        ensureCustomAgentEnabled,
        markWizardCompleted: () => api.post('/api/onboarding/wizard/completed', {}),
        readOnboarded: () =>
          api.get<{ onboarded: boolean; displayName?: string }>('/api/admin/me/onboarded'),
      },
    })
      .then((answer) => {
        queryClient.setQueryData(meKeys.onboarded(), answer);
        queryClient.invalidateQueries({ queryKey: tenantKeys.current() });
        queryClient.invalidateQueries({ queryKey: feedKeys.all });
      })
      .catch((err) => {
        console.error('[onboarding] dev skip failed, opening the wizard:', err);
        window.location.replace('/onboard');
      });
  }, [authed, onboarded, onboardedReady, devSkip, queryClient]);
}
